/* ── Bookmarks Panel ──────────────────────────────────────────
   Displays saved query/response bookmarks from localStorage.
   Supports: toggle bookmark, load bookmark, clear all, export.
   ─────────────────────────────────────────────────────────────── */

import { useCallback, useMemo, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import type { Bookmark } from '../types';

const LS_BOOKMARKS_KEY = 'standards-bookmarks';

/* ── localStorage helpers ────────────────────────────────────── */

export function loadBookmarks(): Bookmark[] {
  try {
    const raw = localStorage.getItem(LS_BOOKMARKS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveBookmarks(bookmarks: Bookmark[]): void {
  try {
    localStorage.setItem(LS_BOOKMARKS_KEY, JSON.stringify(bookmarks));
  } catch { /* quota errors ignored */ }
}

export function isBookmarked(queryId: string): boolean {
  return loadBookmarks().some((b) => b.query_id === queryId);
}

export function toggleBookmark(bookmark: Bookmark): boolean {
  const bookmarks = loadBookmarks();
  const idx = bookmarks.findIndex((b) => b.query_id === bookmark.query_id);
  if (idx >= 0) {
    bookmarks.splice(idx, 1);
    saveBookmarks(bookmarks);
    return false; // removed
  }
  bookmarks.unshift(bookmark);
  saveBookmarks(bookmarks);
  return true; // added
}

/* ── Export bookmarks as markdown ─────────────────────────────── */

function exportBookmarks(bookmarks: Bookmark[]): void {
  const lines = [
    '# Compliance Hub — Saved Bookmarks',
    `Exported: ${new Date().toLocaleString()}`,
    '',
    `Total: ${bookmarks.length} bookmark${bookmarks.length !== 1 ? 's' : ''}`,
    '',
    '---',
    '',
  ];

  for (const b of bookmarks) {
    const date = new Date(b.created_at).toLocaleDateString();
    lines.push(`## ${b.query}`);
    lines.push('');
    lines.push(`**Date:** ${date}`);
    if (b.citations.length > 0) {
      lines.push(`**Standards:** ${b.citations.join(', ')}`);
    }
    lines.push('');
    lines.push(b.response_preview);
    lines.push('');
    lines.push('---');
    lines.push('');
  }

  lines.push('');
  lines.push('*Exported from Compliance Hub — research aid only.*');

  const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `compliance-hub-bookmarks-${new Date().toISOString().slice(0, 10)}.md`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Bookmark icon (star) for inline use in Message ──────────── */

interface BookmarkBtnProps {
  queryId: string;
  query: string;
  responsePreview: string;
  citations: string[];
  onToggle?: (bookmarked: boolean) => void;
}

export function BookmarkButton({ queryId, query, responsePreview, citations, onToggle }: BookmarkBtnProps) {
  const [bookmarked, setBookmarked] = useState(() => isBookmarked(queryId));
  const { addToast } = useToast();

  const handleClick = useCallback(() => {
    const bm: Bookmark = {
      id: crypto.randomUUID(),
      query,
      response_preview: responsePreview.slice(0, 300),
      citations,
      query_id: queryId,
      created_at: new Date().toISOString(),
    };
    const nowBookmarked = toggleBookmark(bm);
    setBookmarked(nowBookmarked);
    onToggle?.(nowBookmarked);
    if (nowBookmarked) {
      addToast('success', 'Saved to Research panel → Bookmarks', 3000);
    } else {
      addToast('info', 'Bookmark removed', 2000);
    }
  }, [queryId, query, responsePreview, citations, onToggle, addToast]);

  return (
    <button
      className={`action-btn bookmark-btn${bookmarked ? ' bookmark-btn--active' : ''}`}
      onClick={handleClick}
      aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark this response'}
      title={bookmarked ? 'Remove bookmark' : 'Bookmark'}
      aria-pressed={bookmarked}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill={bookmarked ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
      {bookmarked ? 'Saved' : 'Save'}
    </button>
  );
}

/* ── Bookmarks Panel (sidebar view) ──────────────────────────── */

interface PanelProps {
  onLoadBookmark: (queryId: string) => void;
  onClose: () => void;
  /** When true, hides own header/close button (parent provides them) */
  embedded?: boolean;
  /** Called when bookmarks change (add/remove/clear) */
  onBookmarkChange?: () => void;
}

export default function BookmarksPanel({ onLoadBookmark, onClose, embedded, onBookmarkChange }: PanelProps) {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>(loadBookmarks);
  const [filter, setFilter] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  const filtered = useMemo(() => {
    if (!filter.trim()) return bookmarks;
    const q = filter.toLowerCase();
    return bookmarks.filter(
      (b) =>
        b.query.toLowerCase().includes(q) ||
        b.response_preview.toLowerCase().includes(q) ||
        b.citations.some((c) => c.toLowerCase().includes(q)),
    );
  }, [bookmarks, filter]);

  const handleRemove = useCallback((queryId: string) => {
    const updated = bookmarks.filter((b) => b.query_id !== queryId);
    saveBookmarks(updated);
    setBookmarks(updated);
    onBookmarkChange?.();
  }, [bookmarks, onBookmarkChange]);

  const handleClearAll = useCallback(() => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    saveBookmarks([]);
    setBookmarks([]);
    setConfirmClear(false);
    onBookmarkChange?.();
  }, [confirmClear, onBookmarkChange]);

  return (
    <div className="bookmarks-panel">
      {!embedded && (
        <div className="bookmarks-header">
          <h3 className="bookmarks-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth={1} aria-hidden="true">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            Bookmarks ({bookmarks.length})
          </h3>
          <button className="bookmarks-close" onClick={onClose} aria-label="Close bookmarks">✕</button>
        </div>
      )}

      {/* Back to chat link — always visible for navigation clarity */}
      <div className="bookmarks-back-hint">
        <button
          className="bookmarks-back-btn"
          onClick={onClose}
          type="button"
        >
          ← Back to chat
        </button>
      </div>

      {/* Actions bar */}
      {bookmarks.length > 0 && (
        <div className="bookmarks-actions">
          <button
            className="bookmarks-action-btn"
            onClick={() => exportBookmarks(bookmarks)}
            title="Export bookmarks as markdown"
          >
            Export
          </button>
          <button
            className={`bookmarks-action-btn bookmarks-action-btn--danger${confirmClear ? ' bookmarks-action-btn--confirm' : ''}`}
            onClick={handleClearAll}
            title={confirmClear ? 'Click again to confirm' : 'Clear all bookmarks'}
          >
            {confirmClear ? 'Confirm clear?' : 'Clear all'}
          </button>
        </div>
      )}

      {/* Filter */}
      {bookmarks.length > 3 && (
        <div className="bookmarks-filter">
          <input
            type="text"
            placeholder="Filter bookmarks…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bookmarks-filter-input"
            aria-label="Filter bookmarks"
          />
        </div>
      )}

      {/* List */}
      <div className="bookmarks-list">
        {bookmarks.length === 0 && (
          <div className="bookmarks-empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true" style={{ opacity: 0.4 }}>
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            <p>No bookmarks yet</p>
            <p className="bookmarks-empty-hint">
              Select text in any answer and click the bookmark icon to save it here.
            </p>
          </div>
        )}

        {bookmarks.length > 0 && filtered.length === 0 && (
          <div className="bookmarks-empty">
            <p>No matching bookmarks</p>
          </div>
        )}

        {filtered.map((b) => (
          <div
            key={b.query_id}
            className="bookmark-card"
            onClick={() => { onLoadBookmark(b.query_id); onClose(); }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { onLoadBookmark(b.query_id); onClose(); }
            }}
          >
            <div className="bookmark-card-query">{b.query}</div>
            <div className="bookmark-card-preview">{b.response_preview}</div>
            <div className="bookmark-card-meta">
              {b.citations.length > 0 && (
                <span className="bookmark-card-citations">
                  {b.citations.slice(0, 3).join(' · ')}
                  {b.citations.length > 3 && ` +${b.citations.length - 3}`}
                </span>
              )}
              <span className="bookmark-card-date">
                {new Date(b.created_at).toLocaleDateString()}
              </span>
            </div>
            <button
              className="bookmark-card-remove"
              onClick={(e) => { e.stopPropagation(); handleRemove(b.query_id); }}
              aria-label="Remove bookmark"
              title="Remove"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
